// src/components/admin/EnforcedSubtitleCard.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { SubtitleAlignItems } from '@/lib/layouts/enforcedSubtitlePosition';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import CampaignCompositePreview, {
  type CompositeWatermark,
} from '@/components/admin/CampaignCompositePreview';

/** Wire shape of the campaign's enforced_subtitle_position JSON field. */
export interface EnforcedSubtitlePositionValue {
  align_items: SubtitleAlignItems;
  vertical_margin_percentage: number;
  locked: boolean;
}

/** Brand-kit anchor presets: same anchors, icons and preset margins as the brand kit editor. */
const ANCHORS: { align: SubtitleAlignItems; presetMargin: number; Icon: typeof AlignVerticalJustifyStart }[] = [
  { align: 'flex-start', presetMargin: 15, Icon: AlignVerticalJustifyStart },
  { align: 'center', presetMargin: 50, Icon: AlignVerticalJustifyCenter },
  { align: 'flex-end', presetMargin: 25, Icon: AlignVerticalJustifyEnd },
];

export interface EnforcedSubtitleCardProps {
  value: EnforcedSubtitlePositionValue | null;
  onChange: (v: EnforcedSubtitlePositionValue | null) => void;
  /** Other campaign enforcements, composed into the preview for consistency. */
  postLayout?: PostLayoutConfig | null;
  watermark?: CompositeWatermark | null;
}

const EnforcedSubtitleCard: React.FC<EnforcedSubtitleCardProps> = ({
  value,
  onChange,
  postLayout,
  watermark,
}) => {
  const { t } = useTranslation();
  const enabled = value !== null;

  const handleToggle = () => {
    onChange(enabled ? null : { align_items: 'flex-end', vertical_margin_percentage: 25, locked: false });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.campaigns.edit.enforcedSubtitle.cardTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('admin.campaigns.edit.enforcedSubtitle.description')}
        </p>

        <div className="flex items-center gap-3">
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            data-testid="enforced-subtitle-enable"
            id="enforced-subtitle-switch"
          />
          <Label htmlFor="enforced-subtitle-switch">
            {t('admin.campaigns.edit.enforcedSubtitle.enable')}
          </Label>
        </div>

        {enabled && value && (
          <div className="flex flex-col sm:flex-row gap-4">
          <CampaignCompositePreview
            testId="subtitle-position-preview"
            label={t('admin.campaigns.edit.enforcedSubtitle.previewLabel')}
            postLayout={postLayout}
            watermark={watermark}
            subtitlePosition={value}
          />
          <div className="space-y-3 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {t('admin.campaigns.edit.enforcedSubtitle.positionLabel')}
              </span>
              <button
                type="button"
                data-testid="lock-subtitlePosition"
                onClick={() => onChange({ ...value, locked: !value.locked })}
                className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                  value.locked
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-muted/30 hover:bg-muted/50'
                }`}
              >
                {value.locked
                  ? t('admin.campaigns.edit.enforcedLayout.locked')
                  : t('admin.campaigns.edit.enforcedLayout.default')}
              </button>
            </div>

            {/* Vertical anchor — same presets as the brand kit editor */}
            <div className="flex gap-1 p-0.5 bg-muted/50 rounded-md max-w-xs">
              {ANCHORS.map(({ align, presetMargin, Icon }) => (
                <button
                  key={align}
                  type="button"
                  data-testid={`subtitle-anchor-${align}`}
                  onClick={() =>
                    onChange({ ...value, align_items: align, vertical_margin_percentage: presetMargin })
                  }
                  className={`flex-1 flex items-center justify-center h-7 rounded-md border transition-colors ${
                    value.align_items === align
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-transparent hover:bg-muted'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>

            {/* Vertical margin relative to the anchor */}
            <div className="space-y-1 max-w-xs">
              <span className="text-xs text-muted-foreground">
                {t('admin.campaigns.edit.enforcedSubtitle.marginLabel')}: {value.vertical_margin_percentage}%
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={value.vertical_margin_percentage}
                  data-testid="subtitle-margin-slider"
                  onChange={(e) =>
                    onChange({ ...value, vertical_margin_percentage: Number(e.target.value) })
                  }
                  className="flex-1 accent-primary"
                />
              </div>
            </div>
          </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EnforcedSubtitleCard;
